import { useEffect, useState } from "react";
import { fetchPage, fetchPublicSettings } from "../lib/api";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Phone, ArrowRight, ShieldCheck, Car, Clock, Star } from "lucide-react";
import Seo from "../components/Seo";
import EnquiryForm from "../components/EnquiryForm";
import { REVIEWS } from "../data/reviews";

const RECENT_TESTIMONIALS = REVIEWS.slice(0, 3);

const HERO_IMG = "https://customer-assets.emergentagent.com/job_lime-booking-hub/artifacts/7qzkvjwv_lime%20header%20image.jpg";

export default function Home() {
  const [page, setPage] = useState(null);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchPage("home")
      .then(setPage)
      .catch(() => {})
      .finally(() => setPageLoaded(true));
    fetchPublicSettings().then(setSettings).catch(() => {});
  }, []);

  const hero = page?.sections?.find(s => s.type === "hero");
  const steps = page?.sections?.find(s => s.type === "steps");
  const intro = page?.sections?.find(s => s.type === "intro");

  // Prefer the CMS-saved schema_json if the admin has set one. Falls back
  // to the hardcoded DrivingSchool block.
  let localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "DrivingSchool",
    "name": "Lime Driving School",
    "image": HERO_IMG,
    "url": typeof window !== 'undefined' ? window.location.origin : '',
    "telephone": "+447472887780",
    "priceRange": "££",
    "areaServed": "Southampton, UK",
    "address": { "@type": "PostalAddress", "addressLocality": "Southampton", "addressCountry": "GB" },
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5", "reviewCount": String(REVIEWS.length) },
  };
  if (page?.schema_json) {
    try {
      const parsed = typeof page.schema_json === "string"
        ? JSON.parse(page.schema_json)
        : page.schema_json;
      if (parsed) localBusinessSchema = parsed;
    } catch (e) {
      // Bad JSON in CMS — keep the default and log so the admin can fix it.
      console.warn("Invalid schema_json in CMS for home page:", e.message);
    }
  }

  return (
    <>
      <Seo
        title={page?.meta_title || "Driving Lessons in Southampton | Lime Driving School"}
        description={page?.meta_description || "Calm, patient driving lessons in Southampton. Manual and automatic — book online today."}
        jsonLd={localBusinessSchema}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#111827]" data-testid="home-hero">
        <div className="absolute inset-0">
          {pageLoaded && (
            <>
              <img src={hero?.image || HERO_IMG}
                   alt={hero?.image_alt || "Lime Driving School in action"}
                   className="w-full h-full object-cover animate-[fadein_400ms_ease-out]" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/10" />
            </>
          )}
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-40 md:pt-32 md:pb-56">
          <div className="max-w-2xl">
            <span className="inline-block text-xs uppercase tracking-[0.2em] font-semibold text-white bg-black/50 backdrop-blur-sm rounded-full px-4 py-1.5 mb-5">
              {hero?.eyebrow || "Driving lessons in Southampton"}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-medium text-white" style={{fontFamily:"Outfit, sans-serif"}}>
              {hero?.title || "Find your freedom with LIME"}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/90 leading-relaxed max-w-xl">
              {hero?.subtitle || "Calm, patient and structured lessons with Akram — the calmest instructor in Southampton."}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/packages" data-testid="hero-book-btn">
                <Button className="bg-[#37B160] hover:bg-[#2D9450] text-white h-12 px-7 text-base active:scale-95 transition-transform">
                  Book your lessons <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
              <a href="tel:07472887780" data-testid="hero-call-btn">
                <Button variant="outline" className="h-12 px-7 text-base border-white/40 text-white bg-transparent hover:bg-white/10 hover:text-white">
                  <Phone className="h-4 w-4 mr-2" /> 07472 887780
                </Button>
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-white/80">
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#37B160]" /> DVSA approved (ADI)</div>
              <div className="flex items-center gap-2"><Star className="h-4 w-4 text-[#37B160]" /> 5★ rated by learners</div>
              <div className="flex items-center gap-2"><Car className="h-4 w-4 text-[#37B160]" /> Manual + Automatic</div>
            </div>
          </div>
        </div>
        {/* BSM-style curved transition into the next section */}
        <svg className="absolute bottom-[-1px] left-0 w-full h-[70px] md:h-[110px]" viewBox="0 0 1440 110" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0,110 L0,60 C360,-20 1080,-20 1440,60 L1440,110 Z" fill="#FDFCFB" />
        </svg>
      </section>

      {/* STEPS */}
      <section className="pt-10 pb-20 md:pt-16 md:pb-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="home-steps">
        <div className="grid md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-6">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">How it works</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-medium mt-3" style={{fontFamily:"Outfit, sans-serif"}}>
              {steps?.title || "4 Steps to driving freedom"}
            </h2>
          </div>
          <div className="md:col-span-6 md:pt-4">
            <p className="text-base text-[#4B5563] leading-relaxed">
              From your provisional licence to your pass certificate — we walk every learner through the same proven path.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {(steps?.items || []).map((s, i) => (
            <div key={i} className="bg-white border border-black/5 rounded-2xl p-6 shadow-sm hover:-translate-y-1 transition-transform" data-testid={`step-${i+1}`}>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#37B160]">Step {s.step}</div>
              <h3 className="text-lg font-medium mt-3" style={{fontFamily:"Outfit, sans-serif"}}>{s.title}</h3>
              <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="py-20 md:py-24 bg-[#F3F4F6]" data-testid="home-products">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">Pricing</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-medium mt-3" style={{fontFamily:"Outfit, sans-serif"}}>
              Lesson packages
            </h2>
            <p className="mt-3 text-[#4B5563] max-w-2xl">Pay-as-you-go or save with multi-hour packages. Discounts apply automatically at checkout.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(() => {
              const tiers = settings?.discount_tiers || [];
              const disc = (slots) => {
                let best = 0;
                tiers.forEach(t => { if (slots >= (t.min_lessons || 0) && (t.discount_per_hour || 0) > best) best = t.discount_per_hour; });
                if (best > 0) return best;
                if (slots >= 10) return settings?.discount_20h || 0;
                if (slots >= 5) return settings?.discount_10h || 0;
                return 0;
              };
              return [
                { key: "standard", title: "Standard 2-Hour Lesson", desc: "Pay as you go", hours: 2, rate: settings?.manual_hourly ?? 35 },
                { key: "package10", title: "10-Hour Package", desc: `Save £${disc(5).toFixed(2)}/hour`, hours: 10, rate: (settings?.manual_hourly ?? 35) - disc(5) },
                { key: "package20", title: "20-Hour Package", desc: `Save £${disc(10).toFixed(2)}/hour`, hours: 20, rate: (settings?.manual_hourly ?? 35) - disc(10) },
                { key: "bespoke", title: "Intensive Courses", desc: "Pass fast — 10, 20 or 35 hours", hours: null, rate: null },
              ];
            })().map(p => {
              const isBespoke = p.key === "bespoke";
              const total = p.rate && p.hours ? p.rate * p.hours : null;
              const productToPackageSlug = { standard: "standard", package10: "10-hour", package20: "20-hour", bespoke: "bespoke" };
              const packageHref = `/packages/${productToPackageSlug[p.key]}`;
              return (
                <div key={p.key} className="bg-white border border-black/5 rounded-2xl p-6 flex flex-col" data-testid={`product-card-${p.key}`}>
                  <h3 className="text-lg font-medium" style={{fontFamily:"Outfit, sans-serif"}}>{p.title}</h3>
                  <p className="text-sm text-[#4B5563] mt-1">{p.desc}</p>
                  <div className="mt-6">
                    {isBespoke ? (
                      <div className="text-2xl font-medium" style={{fontFamily:"Outfit, sans-serif"}}>From £500</div>
                    ) : (
                      <>
                        <div className="text-3xl font-medium" style={{fontFamily:"Outfit, sans-serif"}}>£{total?.toFixed(0)}</div>
                        <div className="text-xs text-[#9CA3AF] mt-1">£{p.rate?.toFixed(2)}/hr · {p.hours} hours total</div>
                      </>
                    )}
                  </div>
                  <div className="mt-6 mt-auto pt-6">
                    {isBespoke ? (
                      <Link to={packageHref} data-testid={`product-${p.key}-cta`}>
                        <Button variant="outline" className="w-full border-black/15">Explore courses</Button>
                      </Link>
                    ) : (
                      <Link to={packageHref} data-testid={`product-${p.key}-cta`}>
                        <Button className="w-full bg-[#37B160] hover:bg-[#2D9450] text-white">View {p.title.split("(")[0]}</Button>
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 md:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="home-intro">
        <div className="grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            {pageLoaded ? (
              <img src={intro?.image || "https://images.pexels.com/photos/14816709/pexels-photo-14816709.jpeg"}
                   alt={intro?.image_alt || "Akram - Lime Driving School instructor"}
                   className="w-full rounded-3xl border border-black/5 shadow-sm aspect-[4/5] object-cover animate-[fadein_400ms_ease-out]" />
            ) : (
              <div className="w-full rounded-3xl border border-black/5 shadow-sm aspect-[4/5] bg-[#F3F4F6]" />
            )}
          </div>
          <div className="md:col-span-7">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">Your instructor</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-medium mt-3" style={{fontFamily:"Outfit, sans-serif"}}>
              {intro?.title || "Hi, I'm Akram."}
            </h2>
            <p className="mt-5 text-base text-[#4B5563] leading-relaxed">
              {intro?.body || "I help Southampton learners find their freedom on the road — calmly, patiently and at your pace."}
            </p>
            <div className="mt-8">
              <Link to="/about" data-testid="intro-about-cta">
                <Button variant="outline" className="border-black/15 hover:bg-black/5">Read my story <ArrowRight className="h-4 w-4 ml-1" /></Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-24 bg-[#111827] text-white" data-testid="home-testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#37B160]">What learners say</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-medium mt-3 text-white" style={{fontFamily:"Outfit, sans-serif"}}>
                {REVIEWS.length}+ verified 5★ reviews on Google
              </h2>
            </div>
            <div className="flex items-baseline gap-3">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, j) => <Star key={j} className="h-5 w-5 fill-[#37B160] text-[#37B160]" />)}
              </div>
              <span className="text-sm text-white/70">5.0 average</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {RECENT_TESTIMONIALS.map((t, i) => (
              <blockquote key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col" data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5 mb-3">{[...Array(5)].map((_,j) => <Star key={j} className="h-4 w-4 fill-[#37B160] text-[#37B160]" />)}</div>
                <p className="text-sm text-white/85 leading-relaxed flex-1">"{t.text}"</p>
                <footer className="mt-4 pt-4 border-t border-white/10 flex items-baseline justify-between gap-2">
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold text-white/90 truncate">{t.name}</span>
                  <span className="text-[10px] uppercase tracking-[0.15em] text-white/40 flex-none">{t.period}</span>
                </footer>
              </blockquote>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/reviews"><Button variant="outline" className="border-white/20 text-white bg-transparent hover:bg-white/10 hover:text-white" data-testid="testimonials-more-btn">Read all {REVIEWS.length} reviews <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
          </div>
        </div>
      </section>

      {/* ENQUIRY */}
      <section className="py-20 md:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" id="enquiry" data-testid="home-enquiry">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">Get in touch</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-medium mt-3" style={{fontFamily:"Outfit, sans-serif"}}>
              Make an enquiry
            </h2>
            <p className="mt-5 text-[#4B5563] leading-relaxed">
              Send us a quick message — Akram replies within one working day. We respect your privacy and will never share your details.
            </p>
            <div className="mt-8 space-y-2 text-sm text-[#4B5563]">
              <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#37B160]" /> 07472 887780</div>
              <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#37B160]" /> Mon–Sat lessons</div>
            </div>
          </div>
          <div className="md:col-span-7">
            <EnquiryForm testIdPrefix="enquiry" />
          </div>
        </div>
      </section>
    </>
  );
}
